import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Users, UserPlus, Heart, Cake } from "lucide-react";

const RELATIONS = ["Child", "Spouse", "Parent", "Sibling", "Other"];

const initials = (name = "") => name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export default function PatientFamily() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [unread, setUnread] = useState(0);
  const [fams, setFams] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newFam, setNewFam] = useState({ name: "", relation: "Child", date_of_birth: "", phone: "" });

  const load = async () => {
    const [t, f, n] = await Promise.all([
      api.get(`/tenants/by-slug/${slug}`),
      api.get("/patient/family"),
      api.get("/patient/notifications"),
    ]);
    setTenant(t.data);
    setFams(f.data);
    setUnread(n.data.filter((x) => !x.read).length);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const resetForm = () => setNewFam({ name: "", relation: "Child", date_of_birth: "", phone: "" });

  const addFamily = async () => {
    if (!newFam.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.post("/patient/family", newFam);
      toast.success(`${newFam.name} added to your household`);
      setOpen(false);
      resetForm();
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not add family member"));
    } finally { setSaving(false); }
  };

  const removeFamily = async (id) => {
    try {
      await api.delete(`/patient/family/${id}`);
      toast.success("Family member removed");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not remove"));
    }
  };

  const links = [
    { to: `/c/${slug}/dashboard`, label: "Dashboard" },
    { to: `/c/${slug}/rewards`, label: "Rewards" },
    { to: `/c/${slug}/family`, label: "Family" },
    { to: `/c/${slug}/refer`, label: "Refer" },
    { to: `/c/${slug}/profile`, label: "Profile" },
  ];

  return (
    <div className="min-h-screen bg-mr-bg">
      <TopBar tenant={tenant} links={links} unread={unread} onUnreadChange={setUnread} />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="label-eyebrow text-mr-accent">My household</span>
            <h1 className="font-display text-4xl font-bold text-mr-primary mt-2">Family accounts</h1>
            <p className="text-mr-secondary mt-2 max-w-xl">Link family members to your account. Your clinic will know they're with you when you visit, so check-ins and care plans are simpler.</p>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-family-btn" className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                <UserPlus className="h-4 w-4 mr-2" /> Add family member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Add family member</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input data-testid="fam-name" value={newFam.name} onChange={(e) => setNewFam({ ...newFam, name: e.target.value })} placeholder="e.g. Sarah Yahia" className="mt-2" />
                </div>
                <div>
                  <Label>Relation</Label>
                  <Select value={newFam.relation} onValueChange={(v) => setNewFam({ ...newFam, relation: v })}>
                    <SelectTrigger data-testid="fam-relation" className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of birth (optional)</Label>
                  <Input data-testid="fam-dob" type="date" value={newFam.date_of_birth} onChange={(e) => setNewFam({ ...newFam, date_of_birth: e.target.value })} className="mt-2" />
                </div>
                <div>
                  <Label>Phone (optional)</Label>
                  <Input data-testid="fam-phone" value={newFam.phone} onChange={(e) => setNewFam({ ...newFam, phone: e.target.value })} placeholder="+971…" className="mt-2" />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="fam-save-btn" onClick={addFamily} disabled={saving} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                  {saving ? "Saving…" : "Add to household"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-mr p-5">
            <div className="flex items-center gap-2 text-mr-secondary text-xs uppercase tracking-wider">
              <Users className="h-3.5 w-3.5" /> Household size
            </div>
            <p className="font-display text-3xl font-bold text-mr-primary mt-2" data-testid="family-count">{fams.length + 1}</p>
            <p className="text-xs text-mr-secondary mt-1">You + {fams.length} {fams.length === 1 ? "member" : "members"}</p>
          </div>
          <div className="bg-white rounded-2xl border border-mr p-5">
            <div className="flex items-center gap-2 text-mr-secondary text-xs uppercase tracking-wider">
              <Heart className="h-3.5 w-3.5" /> Primary
            </div>
            <p className="font-display text-lg font-semibold text-mr-primary mt-2 truncate">{user?.name}</p>
            <p className="text-xs text-mr-secondary mt-1">Earns & redeems for the household</p>
          </div>
          <div className="bg-white rounded-2xl border border-mr p-5">
            <div className="flex items-center gap-2 text-mr-secondary text-xs uppercase tracking-wider">
              <Cake className="h-3.5 w-3.5" /> Birthday bonuses
            </div>
            <p className="font-display text-lg font-semibold text-mr-primary mt-2">Auto-credited</p>
            <p className="text-xs text-mr-secondary mt-1">Each family member's birthday earns you points.</p>
          </div>
        </div>

        {fams.length === 0 ? (
          <div className="bg-white border border-mr rounded-2xl p-12 text-center">
            <div className="h-14 w-14 rounded-full bg-mr-primary/10 flex items-center justify-center mx-auto mb-4">
              <Users className="h-6 w-6 text-mr-primary" />
            </div>
            <p className="font-display text-xl font-semibold text-mr-primary">No family members yet</p>
            <p className="text-mr-secondary text-sm mt-2 max-w-md mx-auto">Add your children, spouse or parents so your clinic recognises them at visits and you earn points on their birthdays.</p>
            <Button data-testid="family-empty-add-btn" onClick={() => setOpen(true)} className="mt-6 rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
              <Plus className="h-4 w-4 mr-2" /> Add your first family member
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="family-list">
            {fams.map((f) => (
              <div key={f.id} data-testid={`family-card-${f.id}`} className="bg-white rounded-2xl border border-mr p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-mr-primary/10 text-mr-primary font-display font-semibold flex items-center justify-center flex-shrink-0">
                  {initials(f.name) || "M"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-mr-primary truncate">{f.name}</p>
                  <p className="text-xs text-mr-secondary truncate">
                    {f.relation}
                    {f.date_of_birth ? ` · ${new Date(f.date_of_birth).toLocaleDateString()}` : ""}
                    {f.phone ? ` · ${f.phone}` : ""}
                  </p>
                </div>
                <Button data-testid={`fam-remove-${f.id}`} variant="ghost" size="icon" className="rounded-full" onClick={() => removeFamily(f.id)} aria-label="Remove">
                  <Trash2 className="h-4 w-4 text-mr-accent" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
